import { useCallback, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { KPI_RANGE_GAP } from 'constants/ppa';
import { getValueFromPercent } from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';

type DragHandle = 'min' | 'max' | null;

type UseKpiRangeDragParams = {
    trackRef: RefObject<HTMLDivElement>;
    dataRange: KpiDataRange;
    min: number;
    max: number;
    onRangeChange: (min: number, max: number) => void;
};

export const useKpiRangeDrag = ({
    trackRef,
    dataRange,
    min,
    max,
    onRangeChange,
}: UseKpiRangeDragParams) => {
    const [activeHandle, setActiveHandle] = useState<DragHandle>(null);

    const resolveValueFromPointer = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            if (!track) return dataRange.defaultMin;
            const { left, width } = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (clientX - left) / width));
            return getValueFromPercent(ratio * 100, dataRange);
        },
        [dataRange, trackRef],
    );

    const startDrag = (handle: DragHandle) => (event: ReactPointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setActiveHandle(handle);
        event.currentTarget.setPointerCapture(event.pointerId);

        const onPointerMove = (e: globalThis.PointerEvent) => {
            const next = resolveValueFromPointer(e.clientX);
            if (handle === 'min') {
                onRangeChange(Math.min(next, max - KPI_RANGE_GAP), max);
            } else {
                onRangeChange(min, Math.max(next, min + KPI_RANGE_GAP));
            }
        };

        const onPointerUp = () => {
            setActiveHandle(null);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    };

    return { activeHandle, startDrag };
};
